import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { confirmForgotPassword } from "@/lib/auth";
import { passwordSchema } from "@/lib/auth/password-schema";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Loader2, MoveLeft, Eye, EyeOff } from "lucide-react";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { YesChefLogo } from "@/components/common/YesChefLogo";

const resetSchema = z
  .object({
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    error: () => t`Passwords do not match`,
    path: ["confirmPassword"],
  });

type ResetForm = z.infer<typeof resetSchema>;

const resetSearchSchema = z.object({
  // Search params can arrive parsed as numbers; coerce so all-digit codes are
  // accepted as strings.
  email: z.coerce.string().optional(),
  code: z.coerce.string().optional(),
});

export const Route = createFileRoute("/(public)/reset-password")({
  component: ResetPasswordComponent,
  validateSearch: (search) => resetSearchSchema.parse(search),
});

function ResetPasswordComponent() {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { email, code } = Route.useSearch();

  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // `defaultValues` matter now that the fields are driven by `FormField`'s
  // controller: without them the inputs would start uncontrolled and React
  // would warn on the first keystroke.
  const form = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    mode: "onChange",
    defaultValues: { newPassword: "", confirmPassword: "" },
  });
  const { handleSubmit, formState: { isValid } } = form;

  const resetMutation = useMutation({
    mutationFn: confirmForgotPassword,
    onSuccess: () => setDone(true),
    onError: (error: Error) => {
      toast.error(error.message || t`Password reset failed`);
    },
  });

  const onSubmit = (data: ResetForm) => {
    if (!email || !code) {
      toast.error(
        t`This reset link is invalid or has expired. Please request a new one.`,
      );
      return;
    }
    resetMutation.mutate({ email, code, newPassword: data.newPassword });
  };

  const isPending = resetMutation.isPending;

  const goToLoginLink = (
    <Link
      to="/login"
      className="flex items-center gap-2 rounded-[10px] px-3 py-2.5 text-sm font-medium transition-opacity hover:opacity-70 text-(--gray-600)"
    >
      <MoveLeft className="h-5 w-5" />
      {i18n._("Go to Login")}
    </Link>
  );

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-between px-8 py-20 bg-(--brand-white)"
    >
      <YesChefLogo />

      {done ? (
        <div className="flex w-full max-w-122 flex-col gap-6 text-center">
          <div className="flex flex-col gap-2">
            <h1
              className="font-semibold leading-tight text-(--brand-black)"
              style={{ fontSize: 48 }}
            >
              {i18n._("Password updated successfully!")}
            </h1>
            <p className="text-base text-(--gray-500)">
              {i18n._("You can now sign in with your new password.")}
            </p>
          </div>
          <Link
            to="/login"
            className="flex h-11 w-full items-center justify-center rounded-[8px] text-sm font-medium text-white transition-opacity hover:opacity-90 bg-(--brand-black)"
          >
            {i18n._("Go to sign in")}
          </Link>
        </div>
      ) : (
        <div className="flex w-full max-w-122 flex-col gap-6">
          <div className="flex flex-col gap-2 text-center">
            <h1
              className="font-semibold leading-tight text-(--brand-black)"
              style={{ fontSize: 48 }}
            >
              {i18n._("Create new password")}
            </h1>
            <p className="text-base text-(--gray-500)">
              {i18n._("Choose a new password for your account.")}
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-2.5"
              noValidate
            >
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Create new password")}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder={i18n._("Create new password...")}
                          className="figma-input pr-10"
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{i18n._("Confirm new password")}</FormLabel>
                    <div className="relative">
                      <FormControl>
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={i18n._("Confirm new password...")}
                          className="figma-input pr-10"
                          disabled={isPending}
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-(--gray-400)"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <button
                type="submit"
                disabled={isPending || !isValid}
                className="mt-1 flex h-11 w-full items-center justify-center rounded-[8px] text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 bg-(--brand-black)"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {i18n._("Saving...")}
                  </>
                ) : (
                  i18n._("Save changes")
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate({ to: "/login" })}
                className="flex h-11 w-full items-center justify-center rounded-[8px] text-sm font-medium transition-opacity hover:opacity-70 text-(--gray-600)"
              >
                {i18n._("Cancel")}
              </button>
            </form>
          </Form>
        </div>
      )}

      <div className="flex justify-center">{goToLoginLink}</div>
    </div>
  );
}
